<template>
  <Stack class="w-full aspect-square" v-if="image.length === 0">
    <img :src="IMAGES.PLACEHOLDER" fetchpriority="low" loading="lazy" alt="Placeholder Image" />
  </Stack>

  <div v-else class="w-full sm:w-auto">
    <!-- Mobile -->
    <div v-if="isMobile" class="w-full">
      <Stack class="relative w-full aspect-square" align="center" justify="center" v-if="image.length === 1">
        <img
          :src="image[0].url_standard"
          :alt="`Product Image ${currentImageIndex - 1}`"
          fetchpriority="high"
          class="w-full h-full object-contain"
          data-sctrack="pdp-mobile-image"
        />
        <Icon
          name="zoom-in"
          class="absolute top-xs right-xs cursor-pointer p-[10px] bg-mkm-blue-default hover:bg-mkm-blue-light rounded-md text-white"
          :size="40"
          @click="openZoomModal(0)"
        />
      </Stack>

      <LazySlider
        v-else
        :slides-per-view="1"
        :space-between="0"
        :draggable="true"
        :autoplay="false"
        display-pagination-dots
        custom-dots-theme="orange-gray"
        :items="reversedImages"
        class="!w-full flex flex-wrap justify-center"
      >
        <swiper-slide
          v-for="(galleryImage, index) in reversedImages"
          :key="index"
          :data-test-id="`GalleryItem-${index}`"
          class="m-auto bg-white !w-[100vw] md:w-full !flex justify-center items-center"
        >
          <Stack class="relative w-full aspect-square" align="center" justify="center">
            <img
              :src="galleryImage.url_standard"
              :alt="`Product Image ${currentImageIndex - 1}`"
              :fetchpriority="index === 0 ? 'high' : 'low'"
              :loading="index === 0 ? 'eager' : 'lazy'"
              class="w-full h-full object-contain"
              :data-sctrack="`pdp-mobile-slider-image-${index}`"
            />
            <Icon
              name="zoom-in"
              class="absolute top-xs right-xs sm:right-xl cursor-pointer p-[10px] bg-mkm-blue-default hover:bg-mkm-blue-light rounded-md text-white"
              :size="40"
              @click="openZoomModal(index)"
            />
          </Stack>
        </swiper-slide>
      </LazySlider>
    </div>

    <!-- Web -->
    <Stack v-else direction="col" gap="xs" class="w-full">
      <div class="relative w-full bg-white aspect-square flex items-center justify-center">
        <img
          :src="image[image.length - 1 - currentImageIndex].url_zoom"
          fetchpriority="high"
          loading="eager"
          :alt="`Product Image Zoom ${currentImageIndex - 1}`"
          class="max-w-full max-h-full"
          data-sctrack="pdp-web-image"
        />
        <button aria-label="Click to zoom" @click="openZoomModal(currentImageIndex)">
          <Icon
            name="zoom-in"
            class="absolute top-sm right-sm cursor-pointer p-xs bg-mkm-blue-default hover:bg-mkm-blue-light rounded-md text-white"
            :size="48"
          />
        </button>
      </div>
      <Stack direction="row" gap="xs" v-if="image.length > 1" class="!hidden sm:!flex">
        <div
          v-for="(galleryImage, index) in reversedImages"
          :key="index"
          :class="[
            'w-[82px] h-[82px] bg-white flex items-center justify-center relative',
            'after:absolute after:top-none after:left-none after:w-full after:h-full after:bg-charcoal-darker after:cursor-pointer',
            'after:opacity-0 after:hover:opacity-30 after:transition-all after:duration-200 after:ease-in-out',
            { 'border-md border-mkm-blue-light': currentImageIndex === index },
          ]"
          @click="currentImageIndex = index"
        >
          <img
            :key="index"
            :src="galleryImage.url_thumbnail"
            alt="Product Image thumbnail"
            fetchpriority="low"
            loading="lazy"
            class="max-h-[80px] object-contain"
            data-sctrack="thumbnail-image"
          />
        </div>
      </Stack>
    </Stack>
  </div>

  <!-- Product Zoomer Modal -->
  <LazyProductZoomer
    v-if="showOverlay"
    :images="reversedImages"
    @close="showOverlay = false"
    :current-image-index="currentImageIndex"
  />
</template>

<script lang="ts" setup>
const SwiperSlide = defineAsyncComponent(() => import("swiper/vue").then((module) => module.SwiperSlide));
import { IMAGES } from "@/constants/images";

const props = defineProps(["image"]);

const showOverlay = ref<boolean>(false);
const currentImageIndex = ref<number>(0);

const { getIsMobile } = useDeviceDetection();
const isMobile = ref(getIsMobile());

const reversedImages = computed(() => props.image.slice().reverse());

const openZoomModal = (index: number) => {
  showOverlay.value = true;
  currentImageIndex.value = index;
};

const updateIsMobile = (event: MediaQueryList | MediaQueryListEvent) => {
  isMobile.value = event.matches;
};

onMounted(() => {
  const mediaQuery = window.matchMedia("(max-width: 768px)");
  mediaQuery.addEventListener("change", updateIsMobile);

  onBeforeUnmount(() => {
    mediaQuery.removeEventListener("change", updateIsMobile);
  });
});
</script>
